import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Text,
  Badge,
  VStack,
  HStack,
  Flex,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { MdOutlineEditNote } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const colors = {
  azulClaro: "rgba(17, 123, 170, 1)",
  azulEscuro: "rgba(15, 79, 109, 1)",
  preto: "rgba(0, 0, 0, 1)",
};

const fontSizePadrao = "2xs";

// --- Type for the grouped table data (original structure) ---
type RegistroAgrupado = {
  Status: string[];
  Ordem: string | null;
  Carga: string | null;
  DT: string | null;
  Movimento: string | null;
  Janela: string | null;
  ObsContratacao: string | null;
  ObsDemandante: string | null;
  DtPrevista: string | null;
  Origem: string | null;
  Destino: string | null;
  Motorista: string | null;
  Placa: string | null;
  Doca: string | null;
  PrevEntrada: string | null;
  DtChegada: string | null;
  HoraChegada: string | null;
  DTEntrada: string | null;
  HoraEntrada: string | null;
  DtSaida: string | null;
  HoraSaida: string | null;
  ObsDescarrego: string | null;
};

// --- Type for the ungrouped table data (new simplified structure) ---
type RegistroDetalhado = {
  Status: string;
  Ordem: string | null;
  Carga: string | null;
  TipoCarga: string | null;
  DtPrevista: string | null;
  Motorista: string | null;
  QtdVol: string | null;
  Placa: string | null;
  DtChegada: string | null;
  HoraChegada: string | null;
  Doca: string | null;
  DtEntrada: string | null;
  HoraEntrada: string | null;
  DataSaida: string | null;
  HoraSaida: string | null;
};

// --- Common Chip component (can be used by both) ---
const Chip = ({
  label,
  value,
  bg = colors.azulClaro,
}: {
  label?: string;
  value: string | null;
  bg?: string;
}) => {
  if (!value) return null;

  return (
    <HStack spacing={1} alignItems="center">
      {label && (
        <Badge
          fontSize="10px"
          fontWeight="bold"
          bg={bg}
          color="white"
          px="1.5"
          py="0.5"
          borderRadius="md"
          minW="24px"
          textAlign="center"
        >
          {label}
        </Badge>
      )}
      <Text fontSize={fontSizePadrao}>{value}</Text>
    </HStack>
  );
};

// --- Grouped Chip Renderer (for 'Todos' type) ---
const renderChipGroup = (
  chips: { label: string; value: string | null; bg?: string }[]
) => (
  <VStack spacing={1} align="start" w="full">
    {chips.map((chip, idx) => (
      <Box key={idx} w="full">
        <Chip label={chip.label} value={chip.value} bg={chip.bg} />
        {idx < chips.length - 1 && <Divider my={0.5} borderColor="gray.400" />}
      </Box>
    ))}
  </VStack>
);

// --- Status Renderer (adapted for both types) ---
const renderStatus = (status: string | string[]) => {
  const statusList = Array.isArray(status) ? status : [status];

  return (
    <VStack align="start" spacing={1}>
      {statusList.map((s, i) => {
        const lower = s.toLowerCase();
        let label = "S";
        let color = "gray";

        if (lower.includes("confirmada")) {
          label = "EC";
          color = "rgba(1, 152, 28, 1)";
        } else if (lower.includes("urgente")) {
          label = "U";
          color = "rgba(229, 62, 62, 1)";
        } else if (lower.includes("baixa")) {
          label = "B";
          color = "rgba(1, 152, 28, 1)";
        } else if (lower.includes("normal")) {
          label = "N";
          color = "rgba(250, 188, 55, 1)";
        } else if (lower.includes("aguar") || lower.includes("chegada")) {
          label = "AC";
          color = "rgba(229, 62, 62, 1)";
        } else if (lower.includes("liberado")) {
          label = "LD";
          color = "rgba(17, 123, 170, 1)";
        } else if (lower.includes("chegada conf")) {
          label = "CC";
          color = "rgba(250, 188, 55, 1)";
        }

        return (
          <HStack key={i} spacing={1} alignItems="center">
            <Badge
              borderRadius={["U", "B", "N"].includes(label) ? "3xl" : "md"}
              fontSize="10px"
              px={["U", "B", "N"].includes(label) ? 1 : 2}
              py="0.5"
              fontWeight="bold"
              bg={color}
              color="white"
              minW={["U", "B", "N"].includes(label) ? "24px" : "28px"}
              textAlign="center"
            >
              {label}
            </Badge>
            <Text fontSize={fontSizePadrao}>{s}</Text>
          </HStack>
        );
      })}
    </VStack>
  );
};

// --- Main TabelaLogistica Component ---
const TabelaLogistica = ({
  data,
  type = "Todos",
  maxHeight = "calc(100vh - 200px)", // Altura máxima customizável
}: {
  data: RegistroAgrupado[] | RegistroDetalhado[];
  type?: "Todos" | "Detalhado";
  maxHeight?: string;
}) => {
  const navigate = useNavigate();

  const handleNavigateToObsCarga = () => {
    navigate("obscarga");
  };

  // Render the grouped table (original)
  const renderGroupedTable = (groupedData: RegistroAgrupado[]) => (
    <Box
      overflow="auto"
      maxHeight={maxHeight}
      border="1px solid"
      borderColor="gray.300"
    >
      <Table variant="striped" size="sm">
        <Thead position="sticky" top={0} bg={colors.azulEscuro} zIndex={1}>
          <Tr>
            {[
              "Status",
              "Ordem | Carga | DT",
              "Movimento | Janela",
              "Obs. Contratação",
              "Obs. Demandante",
              "Dt Prevista | Origem | Destino",
              "Motorista | Placa | Doca",
              "Prev. Entrada | Dt. Chegada | Hora Chegada",
              "DT. Entrada | Hora Entrada",
              "Dt. Saída | Hora Saída",
              "Obs. Descarrego",
            ].map((title, idx) => (
              <Th
                key={idx}
                color="white"
                textAlign="center"
                fontSize={fontSizePadrao}
                px="1"
                py="0.5"
                border="1px solid"
                borderColor="gray.300"
                bg={colors.azulEscuro}
              >
                {title}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody maxHeight={'400px'}>
          {groupedData.map((item, idx) => (
            <Tr key={idx} verticalAlign="middle">
              <Td px="1" py="0">
                <Flex align="center" justify="start">
                  {renderStatus(item.Status)}
                </Flex>
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "O", value: item.Ordem, bg: colors.azulClaro },
                  { label: "C", value: item.Carga, bg: colors.azulEscuro },
                  { label: "DT", value: item.DT, bg: colors.preto },
                ])}
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "M", value: item.Movimento, bg: colors.azulClaro },
                  { label: "J", value: item.Janela, bg: colors.azulEscuro },
                ])}
              </Td>

              <Td px="1" py="0" position="relative">
                <Text fontSize={fontSizePadrao}>{item.ObsContratacao}</Text>
                <IconButton
                  aria-label="Edit Contratação"
                  icon={<MdOutlineEditNote />}
                  size="xs"
                  variant="ghost"
                  onClick={handleNavigateToObsCarga}
                  position="absolute"
                  bottom="0"
                  right="0"
                  minW="unset"
                  height="auto"
                  p="0"
                  _hover={{ bg: "transparent" }}
                  _active={{ bg: "transparent" }}
                />
              </Td>

              <Td px="1" py="0.5" position="relative">
                <Text fontSize={fontSizePadrao}>{item.ObsDemandante}</Text>
                <IconButton
                  aria-label="Edit Demandante"
                  icon={<MdOutlineEditNote />}
                  size="xs"
                  variant="ghost"
                  onClick={handleNavigateToObsCarga}
                  position="absolute"
                  bottom="0"
                  right="0"
                  minW="unset"
                  height="auto"
                  p="0"
                  _hover={{ bg: "transparent" }}
                  _active={{ bg: "transparent" }}
                />
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "DP", value: item.DtPrevista, bg: colors.azulClaro },
                  { label: "O", value: item.Origem, bg: colors.azulEscuro },
                  { label: "D", value: item.Destino, bg: colors.preto },
                ])}
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "M", value: item.Motorista },
                  { label: "P", value: item.Placa, bg: colors.azulEscuro },
                  { label: "DC", value: item.Doca, bg: colors.preto },
                ])}
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "PE", value: item.PrevEntrada },
                  { label: "DC", value: item.DtChegada, bg: colors.azulEscuro },
                  { label: "HC", value: item.HoraChegada, bg: colors.preto },
                ])}
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "DE", value: item.DTEntrada },
                  { label: "HE", value: item.HoraEntrada, bg: colors.azulEscuro },
                ])}
              </Td>

              <Td px="1" py="0">
                {renderChipGroup([
                  { label: "DS", value: item.DtSaida },
                  { label: "HS", value: item.HoraSaida, bg: colors.azulEscuro },
                ])}
              </Td>

              <Td px="1" py="0">
                <Text fontSize={fontSizePadrao}>{item.ObsDescarrego}</Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  const renderDetailedTable = (detailedData: RegistroDetalhado[]) => (
    <Box
      overflow="auto"
      maxHeight={maxHeight}
      border="1px solid"
      borderColor="gray.300"
    >
      <Table variant="striped" size="sm">
        <Thead position="sticky" top={0} bg={colors.azulEscuro} zIndex={1}>
          <Tr>
            {[
              "Status",
              "Ordem",
              "Carga",
              "Tipo Carga",
              "Dt. Prevista",
              "Motorista",
              "Qtd.Vol",
              "Placa",
              "Dt.Chegada",
              "Hora Chegada",
              "Doca",
              "Dt.Entrada",
              "Hora Entrada",
              "Data Saída",
              "Hora Saída",
            ].map((title, idx) => (
              <Th
                key={idx}
                color="white"
                textAlign="center"
                fontSize={fontSizePadrao}
                px="1"
                py="0.5"
                border="1px solid"
                borderColor="gray.300"
                bg={colors.azulEscuro}
              >
                {title}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {detailedData.map((item, idx) => (
            <Tr key={idx} verticalAlign="middle">
              <Td px="1" py="1">
                <Flex align="center" justify="start">
                  {renderStatus(item.Status)}
                </Flex>
              </Td>
              <Td px="1" py="1">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.Ordem} />
                </Flex>
              </Td>
              <Td px="1" py="1">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.Carga} />
                </Flex>
              </Td>
              <Td px="1" py="1">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.TipoCarga} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.DtPrevista} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.Motorista} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.QtdVol} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.Placa} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.DtChegada} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.HoraChegada} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.Doca} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.DtEntrada} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.HoraEntrada} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.DataSaida} />
                </Flex>
              </Td>
              <Td px="1" py="0">
                <Flex align="center" justify="center" w="full">
                  <Chip value={item.HoraSaida} />
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box>
      {type === "Todos"
        ? renderGroupedTable(data as RegistroAgrupado[])
        : renderDetailedTable(data as RegistroDetalhado[])}
    </Box>
  );
};

export default TabelaLogistica;